/**
 * API 服务
 * 提供统一的 API 调用方法
 */

// 使用封装好的 post 方法（包含加密解密逻辑）
import { post, type ApiResponse } from "./index";

import type {
  OAuthLoginParams,
  OAuthLoginData,
  WalletBalanceData,
  BetbyInitInfoData,
  RedPointData,
  BetOrderRecordListParams,
  BetOrderRecordListData,
  SystemConfigData,
  ClientIpData,
} from "./types";

// ==================== API 服务 ====================

/**
 * 授权认证 API
 */
export const oauthApi = {
  /**
   * 用户授权登录
   * @param params 请求参数，包含 session_ticket
   * @param config 请求配置
   */
  login: async (
    params: OAuthLoginParams,
    config?: {
      skipErrorHandler?: boolean;
      skipAuth?: boolean;
      headers?: Record<string, string>;
      options?: Record<string, unknown>;
    }
  ): Promise<ApiResponse<OAuthLoginData>> => {
    return post<OAuthLoginData>("/cus-api/user/oauth/login", params, {
      ...config,
      skipAuth: true, // 登录接口不需要 Token
      encryptionType: "RSA", // 使用 RSA 加密
      headers: {
        ...config?.headers,
      },
      ...(config?.options && { options: config.options }),
    });
  },
  /**
   * 刷新 Token
   * @param refresh_token 刷新令牌
   * @param config 请求配置
   */
  refresh: async (
    refresh_token: string,
    config?: {
      skipErrorHandler?: boolean;
      skipAuth?: boolean;
      headers?: Record<string, string>;
    }
  ): Promise<ApiResponse<OAuthLoginData>> => {
    return post<OAuthLoginData>(
      "/cus-api/user/oauth/refresh",
      { refresh_token },
      {
        ...config,
        skipAuth: true, // 刷新 token 接口不需要 Token
        skipErrorHandler: true, // 刷新 token 接口跳过错误处理，避免无限循环
        encryptionType: "RSA", // 使用 RSA 加密
        headers: {
          // "X-Custom-Header": "custom-value",
          // "X-Request-ID": `req-${Date.now()}`,
          ...config?.headers, // 如果外部传入了 headers，会合并进来
        },
      }
    );
  },
};

/**
 * 钱包相关 API
 */
export const walletApi = {
  /**
   * 查询余额（product_id 由请求头 s-product-id 携带，无需传参）
   * @param config 请求配置
   */
  getBalance: async (config?: {
    skipErrorHandler?: boolean;
    headers?: Record<string, string>;
    options?: Record<string, unknown>;
  }): Promise<ApiResponse<WalletBalanceData>> => {
    return post<WalletBalanceData>(
      "/cus-api/user/wallet/balance",
      {},
      {
        ...config,
      }
    );
  },
};

/**
 * Betby 相关 API
 */
export const betbyApi = {
  /**
   * 获取 Betby 初始化信息
   * @param config 请求配置
   */
  getInitInfo: async (config?: {
    skipErrorHandler?: boolean;
    headers?: Record<string, string>;
    options?: Record<string, unknown>;
  }): Promise<ApiResponse<BetbyInitInfoData>> => {
    return post<BetbyInitInfoData>(
      "/cus-api/betby/init/info",
      {}, // 请求参数为空
      {
        ...config,
      }
    );
  },
};

/**
 * 红点相关 API
 */
export const redPointApi = {
  /**
   * 获取所有红点配置
   * @param config 请求配置
   */
  getAll: async (config?: {
    skipErrorHandler?: boolean;
    headers?: Record<string, string>;
  }): Promise<ApiResponse<RedPointData>> => {
    return post<RedPointData>(
      "/cus-api/config/redPoint/getAll",
      {}, // 请求参数为空
      {
        ...config,
      }
    );
  },
  /**
   * 移除红点
   * @param params 请求参数，包含 type 和 msg_type
   * @param config 请求配置
   */
  reduce: async (
    params: { type: string; msg_type: string },
    config?: {
      skipErrorHandler?: boolean;
      headers?: Record<string, string>;
    }
  ): Promise<ApiResponse<unknown>> => {
    return post<unknown>(
      "/cus-api/config/redPoint/reduceMsgType",
      {
        type: params.type,
        msg_type: params.msg_type,
      },
      {
        ...config,
      }
    );
  },
};

/**
 * 投注订单相关 API
 */
export const betOrderApi = {
  /**
   * 获取投注记录列表
   * @param params 请求参数
   * @param config 请求配置
   */
  getRecordList: async (
    params: BetOrderRecordListParams,
    config?: {
      skipErrorHandler?: boolean;
      headers?: Record<string, string>;
    }
  ): Promise<ApiResponse<BetOrderRecordListData>> => {
    return post<BetOrderRecordListData>(
      "/cus-api/betOrder/sdk/record/list",
      params,
      {
        ...config,
      }
    );
  },
};

/**
 * 系统相关 API
 */
export const systemApi = {
  /**
   * 获取系统配置（包含系统时间）
   * @param config 请求配置
   */
  getConfig: async (config?: {
    skipErrorHandler?: boolean;
    headers?: Record<string, string>;
  }): Promise<ApiResponse<SystemConfigData>> => {
    return post<SystemConfigData>(
      "/cus-api/user/basic/system/config",
      {},
      {
        ...config,
      }
    );
  },
  /**
   * 获取客户端 IP
   * @param config 请求配置
   */
  getClientIp: async (config?: {
    skipErrorHandler?: boolean;
    headers?: Record<string, string>;
  }): Promise<ApiResponse<ClientIpData>> => {
    return post<ClientIpData>(
      "/cus-api/user/basic/client/ip",
      {},
      {
        ...config,
      }
    );
  },
};
